import { AlertTriangle, RefreshCw, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

/**
 * AI Unavailable Notice Component
 * Shows a friendly message when AI generation is temporarily unavailable
 * (e.g., when tokens/credits have run out)
 */
export function AIUnavailableNotice({ 
  onRetry, 
  onContact,
  featureName = "AI generation",
  showRetry = true 
}) {
  return (
    <Card className="p-6 bg-amber-50 border border-amber-200 rounded-2xl">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-6 h-6 text-amber-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-amber-900 mb-1">
            {featureName} temporarily unavailable
          </h3>
          <p className="text-sm text-amber-700 mb-4">
            We're experiencing high demand for our AI features. This is usually resolved quickly. 
            Please try again in a few minutes, or continue using the app's other features.
          </p>
          <div className="flex flex-wrap gap-2">
            {showRetry && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={onRetry}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try again
              </Button>
            )}
            {onContact && (
              <Button
                variant="ghost"
                size="sm"
                className="rounded-full text-amber-700 hover:bg-amber-100"
                onClick={onContact}
              >
                <Mail className="w-4 h-4 mr-2" />
                Contact support
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * AI Limit Reached Notice Component
 * Shows when a user has reached their plan's AI generation limit
 */
export function AILimitReachedNotice({ 
  limitType = "activities", // "activities" or "reports"
  currentPlan = "Basic",
  onUpgrade 
}) {
  const limits = {
    activities: { basic: 20, pro: 50, clinical: 150 },
    reports: { basic: 0, pro: 10, clinical: 50 }
  };

  return (
    <Card className="p-6 bg-blue-50 border border-blue-200 rounded-2xl">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-6 h-6 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-blue-900 mb-1">
            Monthly AI {limitType} limit reached
          </h3>
          <p className="text-sm text-blue-700 mb-2">
            You've used all {limits[limitType][currentPlan.toLowerCase()] || 0} AI {limitType} included in your {currentPlan} plan this month.
          </p>
          <p className="text-sm text-blue-600 mb-4">
            Your limit will reset on the 1st of next month, or you can upgrade for more AI generations.
          </p>
          {onUpgrade && (
            <Button
              size="sm"
              className="rounded-full"
              onClick={onUpgrade}
            >
              Upgrade plan
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * Inline AI Error Message
 * A smaller, inline version for use in dialogs/forms
 */
export function AIErrorInline({ message, onRetry }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm">
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
      <span className="text-amber-700 flex-1">{message || "AI generation temporarily unavailable. Please try again."}</span>
      {onRetry && (
        <button 
          onClick={onRetry}
          className="text-amber-700 hover:text-amber-900 underline"
        >
          Retry
        </button>
      )}
    </div>
  );
}

export default AIUnavailableNotice;
